import type { JavaAnsicht, Katalog, SlotAnsicht } from "@gui-creator/gemeinsam";
import { useState, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode } from "react";
import { ItemBild } from "./ItemBild.tsx";
import { rasterLinks, SLOT } from "./masse.ts";
import { useSpielschrift } from "./schrift.ts";
import { SpielText } from "./SpielText.tsx";
import "./inventar.css";

export type SlotMarke = "liste" | "blaettern" | null;
export type Fremde = { slot: number; farbe: string; kuerzel: string; name: string };

type Props = {
  ansicht: JavaAnsicht;
  katalog: Katalog;
  skala: number;
  markierung?: (index: number) => SlotMarke;
  auswahl?: ReadonlySet<number>;
  fokus?: number | null;
  notizen?: ReadonlySet<number>;
  fremde?: Fremde[];
  onSlot?: (index: number, e: MouseEvent | KeyboardEvent) => void;
  onTaste?: (e: KeyboardEvent) => void;
  slotHuelle?: (index: number, inhalt: ReactNode) => ReactNode;
  beschriftung?: string;
};

const TITELFARBE = "#404040";

export function Inventar({ ansicht, katalog, skala, markierung, auswahl, fokus, notizen, fremde, onSlot, onTaste, slotHuelle, beschriftung }: Props) {
  const glyphen = useSpielschrift();
  const [tipp, setTipp] = useState<{ slot: SlotAnsicht; x: number; y: number } | null>(null);
  const rollen = katalog.farbrollen;
  const kursiv = !katalog.darstellung.kursivAus;
  const reihen = Math.ceil(ansicht.slots.length / ansicht.spalten);
  const links = rasterLinks(ansicht.spalten);
  const hoehe = 17 + reihen * SLOT + 7;

  const zeigeTipp = (s: SlotAnsicht) => (e: PointerEvent) => {
    if (e.pointerType !== "mouse" || s.art === "rahmen") return;
    setTipp({ slot: s, x: e.clientX, y: e.clientY });
  };

  return (
    <div
      className="inventar"
      style={{ ["--s" as string]: skala, width: 176 * skala, height: hoehe * skala }}
      role="grid"
      aria-label={beschriftung ?? "Inventar"}
      aria-rowcount={reihen}
      aria-colcount={ansicht.spalten}
      onKeyDown={onTaste}
    >
      <div className="inventar-titel" style={{ left: 8 * skala, top: 6 * skala }}>
        <SpielText text={ansicht.titel} rollen={rollen} glyphen={glyphen} skala={skala} farbe={TITELFARBE} />
      </div>
      <div className="inventar-raster" style={{ left: links * skala, top: 17 * skala, gridTemplateColumns: `repeat(${ansicht.spalten}, ${SLOT * skala}px)` }}>
        {ansicht.slots.map((s) => {
          const marke = markierung?.(s.index) ?? null;
          const gewaehlt = auswahl?.has(s.index) ?? false;
          const andere = fremde?.filter((f) => f.slot === s.index) ?? [];
          const inhalt = (
            <button
              type="button"
              role="gridcell"
              className={`slot${marke ? ` slot-${marke}` : ""}${gewaehlt ? " slot-gewaehlt" : ""}`}
              data-slot={s.index}
              tabIndex={onSlot ? (fokus === s.index || (fokus == null && s.index === 0) ? 0 : -1) : -1}
              aria-selected={gewaehlt}
              aria-label={`Slot ${s.index}${s.art === "rahmen" ? ", Rahmen" : `, ${s.name.replace(/<[^>]*>/g, "") || s.symbol}`}`}
              onClick={(e) => onSlot?.(s.index, e)}
              onPointerMove={zeigeTipp(s)}
              onPointerLeave={() => setTipp(null)}
            >
              <span className={`slot-item${s.leuchten ? " slot-leuchten" : ""}`}>
                <ItemBild symbol={s.symbol} groesse={16 * skala} />
              </span>
              {s.anzahl > 1 && (
                <span className="slot-anzahl">
                  <SpielText text={String(s.anzahl)} rollen={{}} glyphen={glyphen} skala={skala} farbe="#FFFFFF" schatten />
                </span>
              )}
              {notizen?.has(s.index) && <span className="slot-notiz" aria-label="Notiz an diesem Slot" />}
              {andere.map((f, i) => (
                <span key={f.kuerzel + i} className="slot-fremd" style={{ ["--farbe" as string]: f.farbe }} title={`${f.name} hat diesen Slot gewählt`}>
                  {i === 0 && <span className="slot-fremd-name">{f.kuerzel}</span>}
                </span>
              ))}
            </button>
          );
          return <div key={s.index} className="slot-platz">{slotHuelle ? slotHuelle(s.index, inhalt) : inhalt}</div>;
        })}
      </div>
      {tipp && (
        <div className="tooltip" style={{ left: tipp.x + 14, top: tipp.y - 18 }} role="tooltip">
          <SpielText text={tipp.slot.name || " "} rollen={rollen} glyphen={glyphen} skala={Math.min(2, skala)} farbe="#FFFFFF" schatten kursivStandard={kursiv && tipp.slot.art !== "blaettern"} />
          {tipp.slot.beschreibung.length > 0 && (
            <SpielText text={tipp.slot.beschreibung.join("\n")} rollen={rollen} glyphen={glyphen} skala={Math.min(2, skala)} farbe="#AA00AA" schatten kursivStandard={kursiv} />
          )}
        </div>
      )}
    </div>
  );
}
